import { createInstance, dateReviver } from '@/api';
import {
    EntityModelDependenciesReportDto,
    EntityModelClient,
    EntityModelDetailDto,
    CreateEntityModelDto,
    UpdateEntityModelDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useEntityAttributeStore } from '../entity-attribute/entity-attribute.store';
import { useEntityModelEditStore } from './entity-model-edit.store';
import { ENUM_StatusTypes } from '../../common';
import downloadBlob from '@/helpers/download.helpers';

export interface EntityModelDetailStoreState {
    detail?: EntityModelDetailDto;
    dependenciesReport?: EntityModelDependenciesReportDto;
    loading: boolean;
    reloading: boolean;
}

export const client = new EntityModelClient('', createInstance(), dateReviver);

export const useEntityModelDetailStore = defineStore('configuration-entity-model-detail-store', {
    state: (): EntityModelDetailStoreState => ({
        detail: null,
        loading: false,
        reloading: false
    }),
    getters: {
        isDraft(): boolean {
            return this.detail?.statusId === ENUM_StatusTypes.Draft.id;
        }
    },
    actions: {
        async getDetail(id: number) {
            return await client.getDetail(id);
        },
        async get(id: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            try {
                this.detail = await client.getDetail(id);
                return this.detail;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitymodel.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async getDependenciesReport(id: number) {
            try {
                this.dependenciesReport = await client.getDependenciesReport(id);
                return this.dependenciesReport;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitymodel.detail.error.get.title'), error, false);
            }
        },
        async create(dto: CreateEntityModelDto): Promise<number> {
            this.loading = true;
            let response = null;
            try {
                response = await client.create(dto);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitymodel.create.error.title'), error, false);
            } finally {
                useEntityModelEditStore().closeEdit();
                this.loading = false;
            }
            return response?.id;
        },
        async update(id: number, dto: UpdateEntityModelDto) {
            try {
                this.reloading = true;
                await client.update(id, dto);
                useAlertStore().setSuccess(i18n.global.t('entitymodel.detail.success.update.title'));
                await this.get(id, true);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitymodel.detail.error.update.title'), error, false);
            } finally {
                useEntityModelEditStore().closeEdit();
                this.reloading = false;
            }
        },
        async delete(id: number) {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('entitymodel.detail.success.delete.title'));
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitymodel.detail.error.delete.title'), error, false);
            } finally {
                useEntityModelEditStore().closeEdit();
                this.reloading = false;
            }
        },
        async cascadeDelete(id: number) {
            this.reloading = true;
            try {
                await client.cascadeDelete(id);
                useAlertStore().setSuccess(i18n.global.t('entitymodel.detail.success.delete.title'));
                return true;
            } catch (error: any) {
                useAlertStore().handleException(i18n.global.t('entitymodel.detail.error.delete.title'), error);
            } finally {
                useEntityModelEditStore().closeEdit();
                this.reloading = false;
            }
        },
        async validateCascadeDelete(id: number): Promise<boolean> {
            try {
                const isValid = await client.cascadeDeleteValidate(id);
                return isValid;
            } catch (error) {
                console.error('validateCascadeDelete', error);
                return false;
            }
        },
        async changeStatus(id: number, statusType: string): Promise<boolean> {
            this.reloading = true;
            try {
                await client.changeStatus(id, statusType);
                useAlertStore().setSuccess(i18n.global.t('entitymodel.detail.success.update.title'));
                await this.get(id, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitymodel.detail.error.update.title'), error, false);
                return false;
            } finally {
                this.reloading = false;
            }
        },
        async duplicate(id: number): Promise<number> {
            let response = null;
            try {
                response = await client.duplicate(id);
                useAlertStore().setSuccess(i18n.global.t('entitymodel.detail.success.duplicate.title'));
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('entitymodel.detail.error.duplicate.title'),
                    error,
                    false
                );
            }
            return response?.id;
        },
        async export(id: number): Promise<void> {
            try {
                const file = await client.export(id);

                return new Promise((resolve) => {
                    downloadBlob(file.data, file.fileName, () => {
                        resolve();
                    });
                });
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitymodel.detail.error.export.title'), error, false);
            }
        },
        async import(file: File): Promise<number> {
            let response = null;
            try {
                response = await client.import({ data: file, fileName: file.name });
                useAlertStore().setSuccess(i18n.global.t('entitymodel.detail.success.import.title'));
            } catch (error: any) {
                useAlertStore().handleException(i18n.global.t('entitymodel.detail.error.import.title'), error, false);
            }
            return response?.id;
        },
        clear(id: number) {
            this.detail = null;
            this.dependenciesReport = null;
            useEntityAttributeStore().clear(id);
        }
    }
});
